/**
 * Statut juridique d'un client PME (migration 0035) : champs EXIGÉS selon le statut, et DÉSIGNATION de la
 * partie dans le contrat (« {{designation_partie}} », « {{nom_partie}} »).
 *
 * Module PUR (aucun import, aucun réseau). DEUX COPIES IDENTIQUES, octet pour octet :
 *   - supabase/functions/_shared/designation-partie.ts  (edge function envoyer-contrat-signature)
 *   - web/operateur-ts/src/api/designationPartie.ts     (console : aperçu en direct, champs manquants)
 * Un test (Deno ET vitest) compare les deux fichiers et rejoue les mêmes vecteurs : modifier l'un, c'est
 * modifier l'autre. La règle des champs exigés a une troisième jumelle SQL, `client_identite_manque()`
 * (migration 0035), qui reste juge : la base refuse l'envoi du contrat quoi qu'affiche l'écran.
 *
 * ⚠️ FORMULATIONS À VALIDER PAR LE JURISTE (voir SwiftDeliver/docs/onboarding/contrat-client-pme.md).
 */

export type StatutJuridique = 'societe' | 'entreprenant' | 'personne_physique'
export const STATUTS_JURIDIQUES: readonly StatutJuridique[] = ['societe', 'entreprenant', 'personne_physique']
export const LIBELLE_STATUT_JURIDIQUE: Record<StatutJuridique, string> = {
  societe: 'Société (SARL, SA, SAS…)',
  entreprenant: 'Entreprenant (Acte uniforme OHADA)',
  personne_physique: 'Personne physique',
}

export const FORMES_JURIDIQUES = ['SARL', 'SA', 'SAS', 'SNC', 'GIE', 'SCS', 'autre'] as const
export const QUALITES_REPRESENTANT = ['gerant', 'directeur_general', 'autre'] as const
export const LIBELLE_QUALITE: Record<string, string> = { gerant: 'gérant', directeur_general: 'directeur général', autre: 'autre' }
export const TYPES_PIECE = ['cni', 'passeport', 'carte_nina', 'carte_consulaire', 'autre'] as const
/** Libellé de la pièce d'identité, précédé de son article (« de la … », « du … ») : `titulaire <ceci> n° …`. */
const PIECE_AVEC_ARTICLE: Record<string, string> = {
  cni: 'de la carte nationale d\'identité',
  passeport: 'du passeport',
  carte_nina: 'de la carte NINA',
  carte_consulaire: 'de la carte consulaire',
}
export const LIBELLE_TYPE_PIECE: Record<string, string> = {
  cni: 'Carte nationale d\'identité',
  passeport: 'Passeport',
  carte_nina: 'Carte NINA',
  carte_consulaire: 'Carte consulaire',
  autre: 'Autre pièce d\'identité',
}

/** Les champs de `client_accounts` qui décrivent l'identité juridique (tous facultatifs en lecture). */
export interface IdentiteJuridique {
  statut_juridique?: string | null | undefined
  forme_juridique?: string | null | undefined
  forme_juridique_precision?: string | null | undefined
  raison_sociale?: string | null | undefined
  nom_commercial?: string | null | undefined
  capital_fcfa?: number | string | null | undefined
  rccm?: string | null | undefined
  nif?: string | null | undefined
  numero_declaration_entreprenant?: string | null | undefined
  siege_adresse?: string | null | undefined
  representant_nom?: string | null | undefined
  representant_qualite?: string | null | undefined
  representant_qualite_precision?: string | null | undefined
  personne_nom?: string | null | undefined
  personne_prenoms?: string | null | undefined
  date_naissance?: string | null | undefined
  lieu_naissance?: string | null | undefined
  type_piece?: string | null | undefined
  type_piece_precision?: string | null | undefined
  numero_piece?: string | null | undefined
  adresse_personne?: string | null | undefined
}

/** Nom de chaque clé renvoyée par `identiteManque` (et par la fonction SQL jumelle), en français. */
export const LIBELLE_CHAMP: Record<string, string> = {
  statut_juridique: 'statut juridique',
  raison_sociale: 'raison sociale',
  forme_juridique: 'forme juridique',
  forme_juridique_precision: 'précision de la forme juridique',
  rccm: 'RCCM',
  nif: 'NIF',
  siege_adresse: 'adresse du siège',
  representant_nom: 'nom du représentant légal',
  representant_qualite: 'qualité du représentant',
  representant_qualite_precision: 'précision de la qualité du représentant',
  nom: 'nom de la personne ou nom commercial',
  numero_declaration_entreprenant: 'numéro de déclaration d\'entreprenant',
  adresse: 'adresse',
  personne_nom: 'nom',
  personne_prenoms: 'prénoms',
  type_piece: 'type de pièce d\'identité',
  type_piece_precision: 'précision du type de pièce',
  numero_piece: 'numéro de la pièce d\'identité',
  adresse_personne: 'adresse (domicile)',
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

const texte = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/** 25000 → « 25 000 » (espaces simples : le PDF n'embarque que Helvetica/WinAnsi). */
export function nombreFr(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/** « 1985-03-12 » → « 12 mars 1985 » ; « 1er » le premier du mois ; null si la date n'est pas lisible. */
export function dateNaissanceFr(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(texte(iso))
  if (!m) return null
  const jour = Number(m[3])
  const mois = MOIS[Number(m[2]) - 1]
  if (!mois || jour < 1 || jour > 31) return null
  return `${jour === 1 ? '1er' : jour} ${mois} ${m[1]}`
}

export function statutDe(i: IdentiteJuridique): StatutJuridique | null {
  const s = texte(i.statut_juridique)
  return (STATUTS_JURIDIQUES as readonly string[]).includes(s) ? (s as StatutJuridique) : null
}

/** « Nom Prénoms » si les deux sont connus, sinon chaîne vide. */
function nomPersonne(i: IdentiteJuridique): string {
  const nom = texte(i.personne_nom)
  const prenoms = texte(i.personne_prenoms)
  return nom && prenoms ? `${nom} ${prenoms}` : ''
}

/** Adresse de l'entreprenant : le domicile, à défaut l'adresse de l'établissement. */
function adresseEntreprenant(i: IdentiteJuridique): string {
  return texte(i.adresse_personne) || texte(i.siege_adresse)
}

/**
 * Clés des champs MANQUANTS pour le statut (liste vide = identité complète). Jumelle de
 * `public.client_identite_manque()` : mêmes règles, mêmes clés, même ordre.
 */
export function identiteManque(i: IdentiteJuridique): string[] {
  const statut = statutDe(i)
  const m: string[] = []
  if (!statut) return ['statut_juridique']
  if (statut === 'societe') {
    if (!texte(i.raison_sociale)) m.push('raison_sociale')
    const forme = texte(i.forme_juridique)
    if (!forme) m.push('forme_juridique')
    else if (forme === 'autre' && !texte(i.forme_juridique_precision)) m.push('forme_juridique_precision')
    if (!texte(i.rccm)) m.push('rccm')
    if (!texte(i.nif)) m.push('nif')
    if (!texte(i.siege_adresse)) m.push('siege_adresse')
    if (!texte(i.representant_nom)) m.push('representant_nom')
    const qualite = texte(i.representant_qualite)
    if (!qualite) m.push('representant_qualite')
    else if (qualite === 'autre' && !texte(i.representant_qualite_precision)) m.push('representant_qualite_precision')
  } else if (statut === 'entreprenant') {
    if (!nomPersonne(i) && !texte(i.nom_commercial)) m.push('nom')
    if (!texte(i.numero_declaration_entreprenant)) m.push('numero_declaration_entreprenant')
    if (!adresseEntreprenant(i)) m.push('adresse')
  } else {
    if (!texte(i.personne_nom)) m.push('personne_nom')
    if (!texte(i.personne_prenoms)) m.push('personne_prenoms')
    const type = texte(i.type_piece)
    if (!type) m.push('type_piece')
    else if (type === 'autre' && !texte(i.type_piece_precision)) m.push('type_piece_precision')
    if (!texte(i.numero_piece)) m.push('numero_piece')
    if (!texte(i.adresse_personne)) m.push('adresse_personne')
  }
  return m
}

/** Les mêmes manques, en français (« RCCM », « adresse du siège »…). */
export function libellesManquants(cles: readonly string[]): string[] {
  return cles.map((c) => LIBELLE_CHAMP[c] ?? c)
}

/** Texte de la désignation ; `ph` : les champs manquants deviennent « [libellé] » (aperçu en direct). */
function rendre(i: IdentiteJuridique, statut: StatutJuridique, ph: boolean): string {
  const manque = new Set(ph ? identiteManque(i) : [])
  const c = (cle: string, valeur: string): string => (manque.has(cle) || (ph && !valeur) ? `[${LIBELLE_CHAMP[cle] ?? cle}]` : valeur)

  if (statut === 'societe') {
    const forme = texte(i.forme_juridique) === 'autre'
      ? c('forme_juridique_precision', texte(i.forme_juridique_precision))
      : c('forme_juridique', texte(i.forme_juridique))
    const capital = Number(i.capital_fcfa)
    const auCapital = Number.isFinite(capital) && capital > 0 ? ` au capital de ${nombreFr(capital)} FCFA` : ''
    const q = texte(i.representant_qualite)
    const qualite = q === 'autre'
      ? c('representant_qualite_precision', texte(i.representant_qualite_precision))
      : q ? (LIBELLE_QUALITE[q] ?? q) : c('representant_qualite', '')
    return `La société ${c('raison_sociale', texte(i.raison_sociale))}, ${forme}${auCapital}, immatriculée au Registre du Commerce et du Crédit Mobilier de Bamako sous le numéro ${c('rccm', texte(i.rccm))}, NIF ${c('nif', texte(i.nif))}, dont le siège est situé ${c('siege_adresse', texte(i.siege_adresse))}, représentée par ${c('representant_nom', texte(i.representant_nom))}, en sa qualité de ${qualite},`
  }

  if (statut === 'entreprenant') {
    const personne = nomPersonne(i)
    const commercial = texte(i.nom_commercial)
    const nom = personne || commercial || c('nom', '')
    const nomCommercial = personne && commercial ? `, exerçant sous le nom commercial ${commercial}` : ''
    const nif = texte(i.nif) ? `, NIF ${texte(i.nif)}` : ''
    return `${nom}, entreprenant au sens de l'Acte uniforme OHADA relatif au droit commercial général, déclaré sous le numéro ${c('numero_declaration_entreprenant', texte(i.numero_declaration_entreprenant))}${nomCommercial}${nif}, domicilié(e) ${c('adresse', adresseEntreprenant(i))},`
  }

  const nom = `${c('personne_nom', texte(i.personne_nom))} ${c('personne_prenoms', texte(i.personne_prenoms))}`
  const date = dateNaissanceFr(i.date_naissance)
  const lieu = texte(i.lieu_naissance)
  const naissance = date && lieu ? `, né(e) le ${date} à ${lieu}` : date ? `, né(e) le ${date}` : lieu ? `, né(e) à ${lieu}` : ''
  const type = texte(i.type_piece)
  const piece = type === 'autre'
    ? `de la pièce d'identité (${c('type_piece_precision', texte(i.type_piece_precision))})`
    : PIECE_AVEC_ARTICLE[type] ?? `de ${c('type_piece', type)}`
  return `${nom}${naissance}, titulaire ${piece} n° ${c('numero_piece', texte(i.numero_piece))}, demeurant ${c('adresse_personne', texte(i.adresse_personne))},`
}

/**
 * {{designation_partie}} : la désignation de la partie, terminée par une virgule (le modèle enchaîne
 * « ci-après le « Client » »). Chaîne VIDE tant que l'identité est incomplète : le contrat ne part jamais
 * avec un trou.
 */
export function designationPartie(i: IdentiteJuridique): string {
  const statut = statutDe(i)
  if (!statut || identiteManque(i).length > 0) return ''
  return rendre(i, statut, false)
}

/** Aperçu en direct pour la console : les champs manquants apparaissent entre crochets. Vide sans statut. */
export function apercuDesignation(i: IdentiteJuridique): string {
  const statut = statutDe(i)
  return statut ? rendre(i, statut, true) : ''
}

/** {{nom_partie}} : nom court pour les signatures. Vide si le nom n'est pas connu. */
export function nomPartie(i: IdentiteJuridique): string {
  const statut = statutDe(i)
  if (statut === 'societe') return texte(i.raison_sociale)
  if (statut === 'entreprenant' || statut === 'personne_physique') {
    return nomPersonne(i) || (statut === 'entreprenant' ? texte(i.nom_commercial) : '')
  }
  return ''
}

/** Qui signe : le représentant d'une société, sinon la personne elle-même (nom court pour Documenso, WhatsApp). */
export function nomSignataire(i: IdentiteJuridique): string {
  return statutDe(i) === 'societe' ? texte(i.representant_nom) : nomPartie(i)
}
